import React, { memo, useMemo, Suspense, lazy } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Loading } from 'components/shared'
import {
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_SIMPLE,
    LAYOUT_TYPE_STACKED_SIDE,
    LAYOUT_TYPE_DECKED,
    LAYOUT_TYPE_BLANK,
    LAYOUT_TYPE_BLACK_BLACK
} from 'constants/theme.constant'
import useAuth from 'utils/hooks/useAuth'
import useDirection from 'utils/hooks/useDirection'
import useLocale from 'utils/hooks/useLocale'

// Static lazy imports — do NOT use lazy() inside render
const ClassicLayout = lazy(() => import('./ClassicLayout'))
const ModernLayout = lazy(() => import('./ModernLayout'))
const StackedSideLayout = lazy(() => import('./StackedSideLayout'))
const SimpleLayout = lazy(() => import('./SimpleLayout'))
const DeckedLayout = lazy(() => import('./DeckedLayout'))
const BlankLayout = lazy(() => import('./BlankLayout'))
const AuthLayout = lazy(() => import('./AuthLayout'))
const BlankBlackLayout = lazy(() => import('./AuthLayout/BlankBlackLayout.js'))

const layoutMap = {
    [LAYOUT_TYPE_CLASSIC]: ClassicLayout,
    [LAYOUT_TYPE_MODERN]: ModernLayout,
    [LAYOUT_TYPE_SIMPLE]: SimpleLayout,
    [LAYOUT_TYPE_STACKED_SIDE]: StackedSideLayout,
    [LAYOUT_TYPE_DECKED]: DeckedLayout,
    [LAYOUT_TYPE_BLANK]: BlankLayout,
    [LAYOUT_TYPE_BLACK_BLACK]: BlankBlackLayout,
}

const Layout = () => {
    const layoutType = useSelector((state) => state.theme.layout.type)
    const { authenticated } = useAuth()
    const location = useLocation()

    useDirection()
    useLocale()

    const AppLayout = useMemo(() => {
        const isClientPreview = location.pathname.includes('client_preview')
        const isClientDrafts = location.pathname.includes('client_drafts')
        const isSymphonicSignUp = location.pathname.includes('sign-up-symphonic')
        // The CP signup flow needs the full width for its side-by-side plan
        // cards; the narrower sign-up-tts / sign-up-symphonic funnels keep
        // their own layouts.
        const isCpSignUp = location.pathname.includes('sign-up-connected-partner')
        const isTtsSignUp = location.pathname.includes('sign-up-tts')
        const isTtsPublicReport = location.pathname.includes('/reports/tts')
        const isPublicTrackerLink = location.pathname.startsWith('/reports/tracker-link')
        // Legacy client report — shared with clients by link, so it must render
        // bare rather than inside the authenticated app shell (or the sign-in
        // shell, which is what an anonymous visitor would otherwise land in).
        const isLegacyReport = location.pathname.startsWith('/reports/legacy')
        const isSSoLogin = location.pathname.includes('ssoLogin')
        const isSingleReport = location.pathname.includes('single-report')

        if (isClientPreview && !location.pathname.startsWith('/app/')) {
            return BlankLayout
        }

        if (isClientDrafts || isTtsPublicReport || isPublicTrackerLink || isLegacyReport || isSymphonicSignUp || isCpSignUp || isSSoLogin || isSingleReport) {
            return BlankLayout
        }

        if (!authenticated) {
            return AuthLayout
        }

        return layoutMap[layoutType] || ModernLayout
    }, [location.pathname, authenticated, layoutType])

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            <AppLayout />

        </Suspense>
    )
}

export default memo(Layout)
